import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { createLogger } from '../utils/logger';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const logger = createLogger({ module: 'OnboardingBanner' });

interface OnboardingBannerProps {
  className?: string;
}

const OnboardingBanner: React.FC<OnboardingBannerProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user, updateOnboardingStatus } = useAuth();
  const { showToast } = useToast();

  const handleStartOnboarding = () => {
    navigate('/onboarding');
  };

  const handleDismiss = async () => {
    if (!user) return;
    
    try {
      // Update the onboarding_complete flag in the database
      const { error } = await supabase
        .from('users')
        .update({ onboarding_complete: true })
        .eq('id', user.id);
      
      if (error) throw error;
      
      // Update local state via the AuthContext
      updateOnboardingStatus();
      
      showToast('Onboarding reminder dismissed', 'success');
      
      logger.info('User dismissed onboarding banner', { 
        userId: user.id 
      });
    } catch (error) {
      logger.error('Error dismissing onboarding banner:', error);
      showToast('Failed to dismiss the banner. Please try again.', 'error');
    }
  };

  return (
    <div className={`rounded-lg p-4 bg-blue-50 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 ${className}`}>
      <div className="flex items-start">
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">
            Complete your profile setup
          </h3>
          <div className="mt-2 text-sm">
            <p>
              Our guided onboarding experience will help you set up your professional profile 
              and import your career history.
            </p>
          </div>
          <div className="mt-3">
            <button
              onClick={handleStartOnboarding}
              className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-800 dark:text-blue-100 dark:hover:bg-blue-700"
            >
              Start Onboarding
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
        <button
          type="button"
          className="ml-3 inline-flex rounded-md p-1.5 text-blue-500 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 dark:text-blue-300 dark:hover:bg-blue-800"
          onClick={handleDismiss}
        >
          <span className="sr-only">Dismiss</span>
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default OnboardingBanner; 